import { API_BASE_URL } from "../config/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import EngineerLayout from "../components/EngineerLayout";
import {
  applyThemePreference,
  getStoredThemePreference,
  THEME_OPTIONS
} from "../utils/theme";

const getSignatureUrl = (signature = "") => {
  if (!signature) return "";
  if (signature.startsWith("data:") || signature.startsWith("http")) return signature;
  return `${API_BASE_URL}${signature.startsWith("/") ? signature : `/${signature}`}`;
};

export default function EngineerProfile() {
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    engineerName;
  const [loading, setLoading] = useState(true);
  const signaturePadRef = useRef(null);
  const fullscreenSignatureRef = useRef(null);
  const [profileForm, setProfileForm] = useState({
    engineerId: "",
    name: "",
    email: "",
    phone: "",
    aadhar: "",
    esi: ""
  });
  const [signaturePreview, setSignaturePreview] = useState("");
  const [drawnSignatureData, setDrawnSignatureData] = useState("");
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [signatureMessage, setSignatureMessage] = useState({ text: "", tone: "idle" });
  const [fullscreenPadOpen, setFullscreenPadOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: ""
  });
  const [themePreference, setThemePreference] = useState(() => getStoredThemePreference());

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axios.get(API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}`);
        setProfileForm({
          engineerId: res.data.engineerId || "",
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          aadhar: res.data.aadhar || "",
          esi: res.data.esi || ""
        });
        setSignaturePreview(res.data.signature || "");
      } catch (error) {
        alert("Failed to load engineer profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [engineerLookup]);

  useEffect(() => {
    if (!fullscreenPadOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [fullscreenPadOpen]);

  const saveDrawnSignatureDraft = (padRef = signaturePadRef) => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      setSignatureMessage({ text: "Draw a signature before saving it.", tone: "error" });
      return false;
    }

    const dataUrl = pad.getTrimmedCanvas().toDataURL("image/png");
    setDrawnSignatureData(dataUrl);
    setSignaturePreview(dataUrl);
    setSignatureMessage({ text: "Signature ready. Tap Update Signature to save it permanently.", tone: "success" });
    return true;
  };

  const undoSignatureStroke = (padRef = signaturePadRef) => {
    const pad = padRef.current;
    if (!pad) return;

    const data = pad.toData();
    if (data.length === 0) return;
    pad.fromData(data.slice(0, -1));
  };

  const clearSignatureDraft = () => {
    signaturePadRef.current?.clear();
    fullscreenSignatureRef.current?.clear();
    setDrawnSignatureData("");
    setSignatureMessage({ text: signaturePreview ? "Drawing cleared. Existing saved signature is unchanged." : "", tone: "idle" });
  };

  const updateSignature = async () => {
    if (!drawnSignatureData) {
      setSignatureMessage({ text: "Draw and save a signature before updating your profile.", tone: "error" });
      return;
    }

    try {
      setSignatureSaving(true);
      const payload = new FormData();
      payload.append("name", profileForm.name);
      payload.append("email", profileForm.email);
      payload.append("phone", profileForm.phone);
      payload.append("aadhar", profileForm.aadhar);
      payload.append("esi", profileForm.esi);
      payload.append("signatureData", drawnSignatureData);

      const response = await axios.put(
        API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}`,
        payload
      );

      setSignaturePreview(response.data.signature || drawnSignatureData);
      setDrawnSignatureData("");
      signaturePadRef.current?.clear();
      fullscreenSignatureRef.current?.clear();
      setSignatureMessage({ text: "Signature updated successfully.", tone: "success" });
    } catch (error) {
      setSignatureMessage({ text: error.response?.data?.message || "Failed to update signature.", tone: "error" });
    } finally {
      setSignatureSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}/password`,
        passwordForm
      );
      setPasswordForm({ currentPassword: "", newPassword: "" });
      alert("Password updated successfully");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update password");
    }
  };

  const updateThemePreference = (value) => {
    setThemePreference(value);
    applyThemePreference(value);
  };

  const content = loading ? (
    <div className="p-6">Loading profile...</div>
  ) : (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-sm text-gray-500 mt-1">
          View your details here. Only admin can edit personal information.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Profile Details</h2>
          {[
            ["engineerId", "Engineer ID"],
            ["name", "Name"],
            ["email", "Email"],
            ["phone", "Phone"],
            ["aadhar", "Aadhar"],
            ["esi", "ESI"]
          ].map(([key, label]) => (
            <div key={key}>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
              <input
                value={profileForm[key]}
                readOnly
                className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gray-100 text-gray-500"
              />
            </div>
          ))}
          <div className="rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Contact admin if any of these details need to be changed.
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4 xl:col-span-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">My Signature</h2>
            <p className="text-sm text-gray-500">This saved signature is used automatically in your reports.</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <div className="overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 bg-white shadow-inner">
                <SignatureCanvas
                  ref={signaturePadRef}
                  penColor="#111827"
                  minWidth={0.8}
                  maxWidth={2.8}
                  velocityFilterWeight={0.7}
                  canvasProps={{
                    width: 1200,
                    height: 300,
                    className: "h-44 w-full bg-white",
                    style: { touchAction: "none" }
                  }}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => saveDrawnSignatureDraft(signaturePadRef)} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                  Save Signature
                </button>
                <button type="button" onClick={() => setFullscreenPadOpen(true)} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                  Open Fullscreen Pad
                </button>
                <button type="button" onClick={() => undoSignatureStroke(signaturePadRef)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                  Undo
                </button>
                <button type="button" onClick={clearSignatureDraft} className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Clear Signature
                </button>
              </div>
              {signatureMessage.text && (
                <p className={`mt-3 text-sm font-semibold ${signatureMessage.tone === "error" ? "text-red-600" : "text-emerald-600"}`}>
                  {signatureMessage.text}
                </p>
              )}
            </div>
            <div className="flex min-h-44 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 p-4">
              {signaturePreview ? (
                <img src={getSignatureUrl(signaturePreview)} alt="Saved engineer signature" className="max-h-36 max-w-full object-contain" />
              ) : (
                <p className="text-center text-sm font-medium text-gray-500">No signature saved yet.</p>
              )}
            </div>
          </div>
          <button type="button" disabled={signatureSaving} onClick={updateSignature} className="rounded-xl bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800 disabled:bg-blue-300">
            {signatureSaving ? "Updating Signature..." : "Update Signature"}
          </button>
        </div>

        <form onSubmit={changePassword} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm((current) => ({ ...current, currentPassword: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm((current) => ({ ...current, newPassword: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3"
            />
          </div>
          <button className="bg-gray-900 hover:bg-black text-white px-5 py-3 rounded-xl">
            Update Password
          </button>
        </form>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>
          <p className="text-sm text-gray-500">
            Choose how the app should look on this device.
          </p>
          <div className="space-y-3">
            {THEME_OPTIONS.map((option) => {
              const isActive = themePreference === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateThemePreference(option.value)}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                    isActive
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-medium">{option.label}</span>
                  <span className="text-xs uppercase tracking-wide">
                    {isActive ? "Selected" : "Choose"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {fullscreenPadOpen && (
        <div className="fixed inset-0 z-[70] flex h-[100dvh] flex-col bg-slate-950 text-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
            <div>
              <h2 className="text-base font-bold sm:text-lg">Fullscreen Signature Pad</h2>
              <p className="text-xs text-slate-300">Use finger, stylus, or mouse. Scrolling is disabled while signing.</p>
            </div>
            <button type="button" onClick={() => setFullscreenPadOpen(false)} className="rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20">
              Close
            </button>
          </div>
          <div className="min-h-0 flex-1 p-3 sm:p-4">
            <div className="h-full overflow-hidden rounded-2xl bg-white shadow-inner">
              <SignatureCanvas
                ref={fullscreenSignatureRef}
                penColor="#111827"
                minWidth={1}
                maxWidth={3.2}
                velocityFilterWeight={0.72}
                canvasProps={{
                  width: 1800,
                  height: 1000,
                  className: "h-full w-full bg-white",
                  style: { touchAction: "none" }
                }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-4 py-3">
            <button type="button" onClick={() => fullscreenSignatureRef.current?.clear()} className="rounded-lg bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-600">
              Clear Signature
            </button>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => undoSignatureStroke(fullscreenSignatureRef)} className="rounded-lg bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/20">
                Undo
              </button>
              <button
                type="button"
                onClick={() => {
                  if (saveDrawnSignatureDraft(fullscreenSignatureRef)) {
                    setFullscreenPadOpen(false);
                  }
                }}
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-600"
              >
                Save Signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return <EngineerLayout>{content}</EngineerLayout>;
}


